import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import {ArrowLeft, FileText, Save, Loader2, Check, Globe, EyeOff} from 'lucide-react'

export default function AdminPages() {
  const [pages, setPages] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [editingPage, setEditingPage] = useState<any>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    loadPages()
  }, [])

  const loadPages = async () => {
    try {
      const { list } = await lumi.entities.site_pages.list({
        sort: { slug: 1 },
        limit: 50,
      })
      setPages(list)
    } catch (err) {
      console.error("Erreur:", err)
    } finally {
      setLoading(false)
    }
  }

  const savePage = async () => {
    if (!editingPage?._id) return
    setSaving(true)
    setSaved(false)
    try {
      await lumi.entities.site_pages.update(editingPage._id, {
        title: editingPage.title,
        subtitle: editingPage.subtitle || "",
        content: editingPage.content,
        metaDescription: editingPage.metaDescription || "",
        isPublished: editingPage.isPublished,
        updatedAt: new Date().toISOString(),
      })
      setSaved(true)
      await loadPages()
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      console.error("Erreur sauvegarde:", err)
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field: string, value: any) => {
    setEditingPage((prev: any) => ({ ...prev, [field]: value }))
    setSaved(false)
  }

  return (
    <div className="min-h-screen pt-24 lg:pt-32 pb-20">
      <div className="section-padding">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          Retour au dashboard
        </Link>

        <h1 className="text-2xl font-bold text-white flex items-center gap-2 mb-6">
          <FileText className="w-6 h-6 text-cyan-400" />
          Gestion des pages
        </h1>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Pages list */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Pages du site</h3>
              {pages.map((page) => (
                <button
                  key={page._id}
                  onClick={() => { setEditingPage({ ...page }); setSaved(false) }}
                  className={`w-full text-left card !p-4 group hover:border-cyan-500/40 ${
                    editingPage?._id === page._id ? "!border-cyan-500/50 bg-cyan-500/5" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {page.isPublished ? (
                      <Globe className="w-3.5 h-3.5 text-emerald-400" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5 text-slate-500" />
                    )}
                    <span className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors">
                      {page.title}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">/{page.slug}</p>
                </button>
              ))}
            </div>

            {/* Editor */}
            <div className="lg:col-span-2">
              {editingPage ? (
                <div className="card !p-6 space-y-5 animate-fade-in-up">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">
                      Modifier : {editingPage.title}
                    </h3>
                    {saved && (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                        <Check className="w-3.5 h-3.5" /> Sauvegarde !
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="label-field">Titre de la page</label>
                    <input
                      type="text"
                      value={editingPage.title || ""}
                      onChange={(e) => updateField("title", e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="label-field">Sous-titre</label>
                    <input
                      type="text"
                      value={editingPage.subtitle || ""}
                      onChange={(e) => updateField("subtitle", e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="label-field">Contenu principal</label>
                    <textarea
                      value={editingPage.content || ""}
                      onChange={(e) => updateField("content", e.target.value)}
                      className="input-field min-h-[200px] resize-y"
                    />
                  </div>

                  <div>
                    <label className="label-field">Description SEO (meta description)</label>
                    <textarea
                      value={editingPage.metaDescription || ""}
                      onChange={(e) => updateField("metaDescription", e.target.value)}
                      className="input-field min-h-[80px] resize-y"
                      maxLength={160}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {(editingPage.metaDescription || "").length}/160 caracteres
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingPage.isPublished ?? true}
                        onChange={(e) => updateField("isPublished", e.target.checked)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-sm text-slate-300">Page publiee</span>
                    </label>
                  </div>

                  <button
                    onClick={savePage}
                    disabled={saving}
                    className="btn-primary w-full justify-center disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Sauvegarder les modifications
                  </button>
                </div>
              ) : (
                <div className="card !p-12 text-center">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Selectionnez une page</h3>
                  <p className="text-sm text-slate-400">Cliquez sur une page dans la liste pour la modifier.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
